export interface CardImageSides {
    front?: string | null;
    back?: string | null;
}

export type DownloadCardImageResult =
    | { success: true; method: "share" | "download" }
    | {
          success: false;
          error:
              | "NO_IMAGE_DATA"
              | "INVALID_IMAGE_DATA"
              | "USER_CANCELLED"
              | "UNKNOWN_ERROR";
      };

const DATA_URI_PATTERN = /^data:(image\/[a-zA-Z0-9.+-]+);base64,/;

export function dataUriToFile(dataUri: string, filename: string): File {
    const match = dataUri.match(DATA_URI_PATTERN);
    if (!match) {
        throw new Error("Invalid image data URI");
    }

    const mimeType = match[1];
    const base64Data = dataUri.slice(match[0].length);
    const byteCharacters = atob(base64Data);
    const byteArrays: Uint8Array[] = [];

    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }
        byteArrays.push(new Uint8Array(byteNumbers));
    }

    const blob = new Blob(byteArrays as BlobPart[], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
}

export function isMobileDevice(): boolean {
    const userAgent = window.navigator.userAgent;
    if (/iPhone|iPad|iPod|Android/i.test(userAgent)) {
        return true;
    }

    // iPadOS 13+ Safari reports a desktop-Mac UA by default; detect via touch support.
    if (
        window.navigator.platform === "MacIntel" &&
        window.navigator.maxTouchPoints > 1
    ) {
        return true;
    }

    return false;
}

export function canUseWebShareFiles(files: File[]): boolean {
    const nav = window.navigator as Navigator & {
        canShare?: (data?: ShareData) => boolean;
        share?: (data?: ShareData) => Promise<void>;
    };

    if (typeof nav.share !== "function" || typeof nav.canShare !== "function") {
        return false;
    }

    try {
        return nav.canShare({ files });
    } catch {
        // Some browsers throw from canShare rather than returning false
        // (e.g. for unsupported data) - treat that the same as "cannot share".
        return false;
    }
}

export function triggerBlobDownload(file: File): void {
    const url = URL.createObjectURL(file);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = file.name;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function downloadCardImages(
    sides: CardImageSides
): Promise<DownloadCardImageResult> {
    try {
        const entries: { side: "front" | "back"; dataUri: string }[] = [];
        if (sides.front) entries.push({ side: "front", dataUri: sides.front });
        if (sides.back) entries.push({ side: "back", dataUri: sides.back });

        if (entries.length === 0) {
            return { success: false, error: "NO_IMAGE_DATA" };
        }

        let files: File[];
        try {
            files = entries.map(({ side, dataUri }) =>
                dataUriToFile(dataUri, `id-card-${side}.png`)
            );
        } catch {
            return { success: false, error: "INVALID_IMAGE_DATA" };
        }

        if (isMobileDevice() && canUseWebShareFiles(files)) {
            try {
                await window.navigator.share({ files });
                return { success: true, method: "share" };
            } catch (err) {
                if (err instanceof Error && err.name === "AbortError") {
                    return { success: false, error: "USER_CANCELLED" };
                }
                // Fall through to the blob-download fallback for any other share failure.
            }
        }

        // Desktop Safari silently drops all but the last of several anchor-click
        // downloads triggered synchronously in the same tick, so only the final
        // file (the back of the card) would ever be saved. Spacing the triggers
        // out gives Safari a chance to register each one as a separate download.
        for (let i = 0; i < files.length; i++) {
            if (i > 0) {
                await delay(200);
            }
            triggerBlobDownload(files[i]);
        }
        return { success: true, method: "download" };
    } catch {
        return { success: false, error: "UNKNOWN_ERROR" };
    }
}
